/**
 * quick-buy-smooth.js
 * ---------------------------------------------------------------------------
 * Removes the flash when switching colour in the quick-buy modal.
 *
 * *** NO STRUCTURAL LIQUID CHANGES REQUIRED ***
 * Works against product-quick-buy.liquid exactly as it is. Nothing moves.
 *
 * What the stock theme does on every colour click (QuickBuyModal.loadProduct):
 *   1. .quick-buy gets style.opacity = 0.5        <- the visible "blink"
 *   2. fetch('/products/<handle>')                <- full page, NO cache
 *   3. DOMParser.parseFromString(entire document)
 *   4. this.replaceChildren(...)                  <- every node destroyed,
 *                                                    including every <img>
 *
 * What this does instead:
 *   - no opacity dim
 *   - cached, de-duplicated, abortable fetch + hover/idle prefetch
 *   - the LIVE gallery element is carried over into the new markup instead of
 *     being rebuilt, so <img> nodes keep their decoded bitmaps. Their src is
 *     updated in place, and only after the new hero has finished decode().
 *   - scroll saved/restored synchronously in the same task, so no intermediate
 *     state is ever painted
 *
 * Install: assets/quick-buy-smooth.js, loaded in theme.liquid AFTER theme.js
 */
(function () {
  'use strict';

  if (window.__medicloQuickBuySmooth) return;
  window.__medicloQuickBuySmooth = true;

  var MODAL_SEL = 'quick-buy-modal, sale-quick-add-modal';
  var SLIDE     = '.quick-buy__slide, .sale-quick-add__slide';
  var GALLERY   = '.quick-buy__gallery, .sale-quick-add__gallery';
  var DOTS      = '[data-quick-buy-dots]';

  /* ---------------------------------------------------------------- *
   * 1. Document cache                                                  *
   *    theme.js uses cachedFetch() when the modal first opens, but a    *
   *    plain fetch() for colour switches. This gives switches a cache.  *
   * ---------------------------------------------------------------- */

  var docCache = new Map();   // url -> Promise<Document>
  var MAX_CACHE = 40;

  // Leave '' for now. Once templates/product.quick-buy.liquid exists, set to
  // '?view=quick-buy' to fetch ~15KB instead of the entire product page.
  var VIEW_PARAM = '';

  function productUrl(handle) {
    return '/products/' + handle + VIEW_PARAM;
  }

  /* ---------------------------------------------------------------- *
   * 1b. Remembering Size / Length across a colour switch               *
   *                                                                    *
   *     option-value.liquid renders  value="{{ option_value.id }}"  —   *
   *     an ID that is unique PER PRODUCT. Since every colour here is a  *
   *     separate product, those IDs do not transfer, which is why       *
   *     ?option_values= silently fell back to the first variant (XXS).  *
   *     So we remember by visible LABEL text and re-match on the new    *
   *     product instead.                                                *
   * ---------------------------------------------------------------- */

  function labelTextFor(root, input) {
    var label = null;
    if (input.id) {
      try { label = root.querySelector('label[for="' + CSS.escape(input.id) + '"]'); } catch (e) {}
    }
    if (!label) label = input.closest('label');
    if (!label && input.parentElement) {
      label = input.parentElement.querySelector('label, a.block-swatch, a.color-swatch, a.thumbnail-swatch');
    }
    return label ? (label.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  // Skip the colour option itself — colour IS the product switch.
  function isColourInput(input) {
    if (input.hasAttribute('data-product-url')) return true;
    var fs = input.closest('fieldset');
    return !!(fs && fs.classList.contains('quick-buy-native-color-option'));
  }

  function rememberOptions(modal) {
    var out = [];
    modal.querySelectorAll('input[data-option-position]:checked').forEach(function (input) {
      if (isColourInput(input)) return;
      var label = labelTextFor(modal, input);
      if (!label) return;
      out.push({ position: input.getAttribute('data-option-position'), label: label });
    });
    return out.length ? out : null;
  }

  /**
   * Re-check the remembered Size / Length inside the freshly fetched markup,
   * BEFORE it is inserted — so the correct option is selected in the very
   * first painted frame rather than flashing the default and correcting.
   * Returns the inputs that were applied, with availability.
   */
  function applyRememberedOptions(root, remembered) {
    if (!remembered) return [];
    var applied = [];

    remembered.forEach(function (want) {
      var candidates = Array.prototype.slice.call(
        root.querySelectorAll('input[data-option-position="' + want.position + '"]')
      ).filter(function (i) { return !isColourInput(i); });

      var match = candidates.find(function (i) {
        return labelTextFor(root, i).toLowerCase() === want.label.toLowerCase();
      });
      if (!match) return;      // this colour has no such size at all

      candidates.forEach(function (i) { i.checked = false; i.removeAttribute('checked'); });
      match.checked = true;
      match.setAttribute('checked', 'checked');

      // "Size: M" readout is server-rendered, so correct it to match
      var fs = match.closest('fieldset');
      var readout = fs && fs.querySelector('.variant-picker__option-info .h-stack span');
      if (readout) {
        readout.textContent = want.label;
        readout.style.visibility = '';
        readout.removeAttribute('data-force-unselected');
      }

      var label = match.id ? root.querySelector('label[for="' + CSS.escape(match.id) + '"]') : null;
      applied.push({
        input: match,
        label: want.label,
        soldOut: !!(label && label.classList.contains('is-disabled'))
      });
    });

    return applied;
  }

  function fetchDoc(url, signal) {
    if (docCache.has(url)) return docCache.get(url);

    var promise = fetch(url, { signal: signal })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status + ' for ' + url);
        return res.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      })
      .catch(function (err) {
        docCache.delete(url);          // never cache a failure
        throw err;
      });

    docCache.set(url, promise);
    if (docCache.size > MAX_CACHE) docCache.delete(docCache.keys().next().value);
    return promise;
  }

  function prefetch(handle) {
    if (!handle) return;
    var url = productUrl(handle);
    if (!docCache.has(url)) fetchDoc(url).catch(function () {});
  }

  /* ---------------------------------------------------------------- *
   * 2. Scroll preservation                                             *
   *    Cheap version of the old inline patch: a handful of known        *
   *    containers read by selector, no querySelectorAll('*') and no     *
   *    getComputedStyle sweep, restored synchronously in the same task. *
   * ---------------------------------------------------------------- */

  var SCROLLABLE = ['.quick-buy', '.quick-buy__content', '.quick-buy__info'];

  function saveScroll(modal) {
    var state = {
      windowX: window.scrollX || 0,
      windowY: window.scrollY || 0,
      modalTop: modal.scrollTop,
      modalLeft: modal.scrollLeft,
      regions: []
    };
    SCROLLABLE.forEach(function (sel) {
      var el = modal.querySelector(sel);
      if (el && (el.scrollTop || el.scrollLeft)) {
        state.regions.push({ sel: sel, top: el.scrollTop, left: el.scrollLeft });
      }
    });
    return state;
  }

  function restoreScroll(modal, state) {
    if (!state) return;
    modal.scrollTop = state.modalTop;
    modal.scrollLeft = state.modalLeft;
    state.regions.forEach(function (r) {
      var el = modal.querySelector(r.sel);   // node is new, selector is stable
      if (el) { el.scrollTop = r.top; el.scrollLeft = r.left; }
    });
    if (window.scrollX !== state.windowX || window.scrollY !== state.windowY) {
      window.scrollTo(state.windowX, state.windowY);
    }
  }

  /* ---------------------------------------------------------------- *
   * 3. Gallery: update the LIVE nodes, never rebuild them               *
   * ---------------------------------------------------------------- */

  function decodeFirst(src, srcset) {
    if (!src) return Promise.resolve();
    var probe = new Image();
    if (srcset) probe.srcset = srcset;
    probe.src = src;
    // decode() can reject on odd cross-origin cases; never let that block us
    return (probe.decode ? probe.decode() : Promise.resolve()).catch(function () {});
  }

  function updateLiveGallery(liveGallery, nextGallery) {
    if (!liveGallery || !nextGallery) return Promise.resolve();

    var nextSlides = Array.prototype.slice.call(nextGallery.querySelectorAll(SLIDE));
    if (!nextSlides.length) return Promise.resolve();

    var hero = nextSlides[0].querySelector('img');

    return decodeFirst(
      hero && hero.getAttribute('src'),
      hero && hero.getAttribute('srcset')
    ).then(function () {
      var carousel = liveGallery.querySelector('[data-quick-buy-carousel]') || liveGallery;
      var liveSlides = Array.prototype.slice.call(liveGallery.querySelectorAll(SLIDE));

      nextSlides.forEach(function (nextSlide, i) {
        var nextImg = nextSlide.querySelector('img');
        var liveSlide = liveSlides[i];

        if (!liveSlide) {                        // new product has MORE media
          nextSlide.classList.toggle('is-active', i === 0);
          carousel.appendChild(nextSlide);
          return;
        }

        var liveImg = liveSlide.querySelector('img');
        if (liveImg && nextImg) {
          var set = nextImg.getAttribute('srcset') || '';
          var src = nextImg.getAttribute('src') || '';
          // Only touch what differs. Media shared between sibling colours
          // (size chart, fabric shot) does not reload at all.
          if (liveImg.getAttribute('srcset') !== set) liveImg.srcset = set;
          if (liveImg.getAttribute('src') !== src) liveImg.src = src;
          if (liveImg.alt !== nextImg.alt) liveImg.alt = nextImg.alt || '';
          liveImg.loading = 'eager';
        } else {
          liveSlide.replaceChildren.apply(
            liveSlide, Array.prototype.slice.call(nextSlide.childNodes)
          );
        }

        liveSlide.dataset.index = i;
        liveSlide.classList.toggle('is-active', i === 0);
      });

      liveSlides.slice(nextSlides.length).forEach(function (s) { s.remove(); });

      var liveDots = liveGallery.querySelector(DOTS);
      var nextDots = nextGallery.querySelector(DOTS);
      if (liveDots && nextDots && liveDots.innerHTML !== nextDots.innerHTML) {
        liveDots.replaceChildren.apply(
          liveDots, Array.prototype.slice.call(nextDots.childNodes)
        );
      }
      if (liveDots) {
        liveDots.querySelectorAll('[data-dot]').forEach(function (d, i) {
          d.classList.toggle('is-active', i === 0);
        });
      }
    });
  }

  /* ---------------------------------------------------------------- *
   * 4. Replacement loadProduct()                                       *
   * ---------------------------------------------------------------- */

  function makeLoadProduct() {
    return async function loadProduct(handle) {
      if (!handle) return;

      // Supersede any switch still in flight; last click wins.
      if (this.__qbAbort) this.__qbAbort.abort();
      this.__qbAbort = new AbortController();
      var signal = this.__qbAbort.signal;
      var token = (this.__qbToken = (this.__qbToken || 0) + 1);

      // Hook for CSS if you want a cursor change. Deliberately NOT opacity.
      this.setAttribute('data-quick-buy-loading', '');

      // Remember Size / Length BEFORE anything is torn down.
      var keepOptions = rememberOptions(this);

      try {
        var doc = await fetchDoc(productUrl(handle), signal);
        if (signal.aborted || token !== this.__qbToken) return;

        var tpl = doc.getElementById('quick-buy-content');
        if (!tpl) throw new Error('#quick-buy-content missing for ' + handle);

        var next = tpl.content.cloneNode(true).firstElementChild;

        // Re-select the remembered Size / Length in the new markup up front,
        // so the first painted frame already shows it.
        var applied = applyRememberedOptions(next, keepOptions);

        var liveGallery = this.querySelector(GALLERY);
        var nextGallery = next.querySelector(GALLERY);

        // 1) Bring the live gallery up to date, waiting for the new hero to
        //    decode. Still attached and visible here, and nothing flickers,
        //    because the bitmaps are ready before any src is assigned.
        await updateLiveGallery(liveGallery, nextGallery);
        if (signal.aborted || token !== this.__qbToken) return;

        // 2) Decode images inside the replacement markup too (mobile thumb),
        //    so that subtree cannot paint empty either.
        await Promise.all(
          Array.prototype.slice.call(next.querySelectorAll('img'), 0, 4)
            .map(function (img) {
              return decodeFirst(img.getAttribute('src'), img.getAttribute('srcset'));
            })
        );
        if (signal.aborted || token !== this.__qbToken) return;

        /* --- one synchronous block from here: no await, so the browser
               cannot paint an intermediate state --------------------------- */

        var scroll = saveScroll(this);

        // Carry the live, already-correct gallery into the new markup so its
        // <img> nodes survive. Moving a node does not re-fetch or re-decode.
        if (liveGallery && nextGallery) nextGallery.replaceWith(liveGallery);

        this.replaceChildren(next);
        this.setAttribute('product-url', '/products/' + handle);

        restoreScroll(this, scroll);

        reconcileSelection(this, applied);

        // initEmbroidery() relocates #embroidery-modal to <body>, so a stale
        // copy has to go before the new one is re-homed.
        try {
          document.body.querySelectorAll(':scope > #embroidery-modal')
            .forEach(function (n) { n.remove(); });
          if (typeof this.initEmbroidery === 'function' && window.jQuery) {
            this.initEmbroidery();
          }
        } catch (err) {
          console.warn('[quick-buy-smooth] embroidery re-init skipped:', err);
        }

        idlePrefetchSiblings(this);

      } catch (err) {
        if (err && err.name === 'AbortError') return;
        console.error('[quick-buy-smooth] falling back to stock loadProduct:', err);
        if (typeof this.__qbOriginalLoadProduct === 'function') {
          return this.__qbOriginalLoadProduct(handle);
        }
      } finally {
        if (token === this.__qbToken) this.removeAttribute('data-quick-buy-loading');
      }
    };
  }

  /**
   * After insertion, tell the theme which variant is really selected so price,
   * inventory and the buy button come from the server rather than from our
   * client-side tick. VariantPicker handles this via its own preload cache, and
   * because the size input carries no data-product-url it takes the PARTIAL
   * re-render path — the gallery is never touched.
   */
  function reconcileSelection(modal, applied) {
    if (!applied || !applied.length) return;

    applied.forEach(function (a) {
      var live = modal.querySelector(
        'input[data-option-position="' + a.input.getAttribute('data-option-position') + '"][value="' +
        a.input.getAttribute('value') + '"]'
      );
      if (!live) return;

      if (!live.checked) live.checked = true;

      // A sold-out size stays visibly selected, but must not look buyable
      // in the window before the server-rendered buy button arrives.
      if (a.soldOut) disableBuyButton(modal);

      live.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function disableBuyButton(modal) {
    modal.querySelectorAll(
      'buy-buttons button[type="submit"], .quick-buy__actions button[type="submit"]'
    ).forEach(function (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
    });
  }

  /* ---------------------------------------------------------------- *
   * 5. Prefetch on hover / touch, and on idle after the modal opens     *
   * ---------------------------------------------------------------- */

  function siblingHandles(modal) {
    var out = [];
    modal.querySelectorAll('[data-product-handle]').forEach(function (el) {
      var h = el.dataset.productHandle;
      if (h && out.indexOf(h) === -1) out.push(h);
    });
    return out;
  }

  function idlePrefetchSiblings(modal) {
    var run = function () { siblingHandles(modal).forEach(prefetch); };
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 3000 });
    else setTimeout(run, 1200);
  }

  function onHover(event) {
    var el = event.target.closest && event.target.closest('[data-product-handle]');
    if (!el || !el.closest(MODAL_SEL)) return;
    if (el.classList.contains('is-selected') || el.classList.contains('is-active')) return;
    prefetch(el.dataset.productHandle);
  }

  document.addEventListener('pointerenter', onHover, true);
  document.addEventListener('touchstart', onHover, { capture: true, passive: true });

  /* ---------------------------------------------------------------- *
   * 6. Install                                                         *
   * ---------------------------------------------------------------- */

  ['quick-buy-modal', 'sale-quick-add-modal'].forEach(function (tag) {
    customElements.whenDefined(tag).then(function () {
      var Ctor = customElements.get(tag);
      if (!Ctor || !Ctor.prototype.loadProduct) {
        console.warn('[quick-buy-smooth] no loadProduct on <' + tag + '>, skipping');
        return;
      }
      if (Ctor.prototype.__qbPatched) return;

      Ctor.prototype.__qbOriginalLoadProduct = Ctor.prototype.loadProduct;
      Ctor.prototype.loadProduct = makeLoadProduct();
      Ctor.prototype.__qbPatched = true;

     

       /*  console.info('[quick-buy-smooth] patched <' + tag + '>');               */
    });
  });

  document.addEventListener('dialog:after-show', function (event) {
    var modal = event.target.closest && event.target.closest(MODAL_SEL);
    if (modal) idlePrefetchSiblings(modal);
  }, true);
})();